import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, variant: ToastVariant = 'success') => {
      const id = ++counter;
      setToasts((prev) => [...prev, { id, message, variant }]);
      window.setTimeout(() => remove(id), 3500);
    },
    [remove]
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => {
          const Icon =
            t.variant === 'success'
              ? CheckCircle2
              : t.variant === 'error'
                ? AlertTriangle
                : Info;
          const chip =
            t.variant === 'success'
              ? 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-300'
              : t.variant === 'error'
                ? 'bg-rose-500/15 text-rose-500 dark:text-rose-300'
                : 'bg-sky-500/15 text-sky-500 dark:text-sky-300';
          return (
            <div
              key={t.id}
              className="pointer-events-auto flex max-w-sm items-center gap-2.5 rounded-xl border border-slate-200 bg-white/90 px-3 py-2.5 text-sm text-slate-700 shadow-xl shadow-slate-900/10 backdrop-blur-xl animate-slide-up dark:border-white/10 dark:bg-navy-850/90 dark:text-slate-200 dark:shadow-black/40"
              role="status"
            >
              <span
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg ${chip}`}
              >
                <Icon className="w-4 h-4" />
              </span>
              <span className="flex-1 font-medium">{t.message}</span>
              <button
                onClick={() => remove(t.id)}
                className="text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-white"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
